import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { clearSessionCaches } from 'core/utils/clearSessionCaches';
import { getByodAppSession } from 'core/utils/byodAppSession';
import { ROUTES } from 'module/navigation/enums/routes.enums';
// import { ROUTES } from 'module/navigation/enums/routes.enums';

/**
 * Attaches request and response interceptors to the given Axios instance.
 * Should be called once per instance (typically inside a service constructor).
 * @param httpClient - The Axios instance to configure.
 */
export function initializeApiInterceptors(httpClient: AxiosInstance): void {
	interceptRequest(httpClient);
	interceptResponse(httpClient);
}

/**
 * Read a field from the BYOD-app session blob (DATA-002: parse-safe).
 */
export function getTokenFromLocalStorage(option: string): any {
	const parsed = getByodAppSession();
	if (parsed && option in parsed) {
		return parsed[option];
	}
	return '';
}

/**
 * Appends a Bearer token and JSON-LD Accept header to an outgoing request config.
 * @param config - The Axios request config to mutate.
 * @param token - The JWT bearer token string.
 */
function appendToken(config: AxiosRequestConfig, token: string): void {
	config.headers.Authorization = `Bearer ${token}`;
	config.headers.Accept = 'application/ld+json';
}

/**
 * Registers a request interceptor that injects auth and tenant headers on every outgoing request:
 * - `Authorization: Bearer <token>` when a token exists in localStorage.
 * - `X-Tenant` set to the stored organisation ID.
 * - `X-Workplace` set to the stored workplace ID.
 * @param httpClient - The Axios instance to attach the interceptor to.
 */
function interceptRequest(httpClient: AxiosInstance): void {
	httpClient.interceptors.request.use(
		(config) => {
			// check token and append it
			const token = getTokenFromLocalStorage('token_key');
			token && appendToken(config, token);

			const organizationId = localStorage.getItem('BYOD-organizationId');
			// console.log('organizationId', organizationId);
			if (organizationId) {
				config.headers['X-Tenant'] = organizationId;
			}

			const workplaceId = localStorage.getItem('workplace-id');
			if (workplaceId) {
				config.headers['X-Workplace'] = workplaceId;
			}

			return config;
		},
		(error) => {
			return Promise.reject(error);
		}
	);
}

/**
 * Registers a response interceptor that handles HTTP error codes globally.
 * - **401**: Clears session caches (auth, schedule week, selected dates) and redirects to login.
 * - **404**: Placeholder for future handling.
 * - **Network Error**: Logs a descriptive error to the console.
 * @param httpClient - The Axios instance to attach the interceptor to.
 */
function interceptResponse(httpClient: AxiosInstance): void {
	httpClient.interceptors.response.use(
		(response) => response,
		(error) => {
			if (!error.response) {
				if (error.message === 'Network Error') {
					console.error('Network error - server is probably down');
				} else {
					console.error('Unkown error', error);
				}
			} else {
				switch (error.response.status) {
					case 401: {
						const requestUrl = String(error.config?.url || '');
						const isLoginRequest = requestUrl.includes('/token-web');
						if (!isLoginRequest) {
							clearSessionCaches();
							if (window.location.pathname !== ROUTES.LOGIN) {
								window.location.href = ROUTES.LOGIN;
							}
						}
						break;
					}
					case 404:
						// add later on
						// window.location.href = ROUTES.LOGIN;
						// on token expire
						// if (!sessionQuery.refreshToken) {
						//     window.location.href = ROUTES.LOGIN;
						// } else {
						//     return sessionAPIService
						//         .refreshToken(sessionQuery.refreshToken)
						//         .then((data) => sessionStore.handleToken(data))
						//         .then(() => {
						//             sessionQuery.token &&
						//                 appendToken(
						//                     error.config,
						//                     sessionQuery.token
						//                 );
						//             return axios.request(error.config);
						//         });
						// }
						break;
				}
			}

			// Always reject so callers hit `.catch` / try-catch (REL-002).
			// Never return `error.response` — that resolves the promise as success.
			return Promise.reject(error);
		}
	);
}
